import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { provisionPropertyApi, uploadPropertyDocumentApi } from '../../Api/AdminApiCall';

const extractUploadUrl = (response) => {
  const body = response?.data;
  if (!body) return null;

  const candidates = [
    body?.data?.url,
    body?.data?.uploadUrl,
    body?.data?.location,
    body?.data?.signedUrl,
    body?.data?.fileUrl,
    body?.data?.documentUrl,
    body?.data?.s3Url,
    body?.data?.file_url,
    body?.url,
    body?.uploadUrl,
    body?.location,
    body?.signedUrl,
    body?.fileUrl,
    body?.documentUrl,
    body?.s3Url,
    body?.file_url,
    typeof body?.data === 'string' ? body.data : null,
  ];

  return candidates.find((url) => typeof url === 'string' && url.trim().length > 0) || null;
};

// Upload documents with metadata preserved (docType, category)
export const uploadDocuments = createAsyncThunk(
  'properties/uploadDocuments',
  async (uploadItems, { rejectWithValue }) => {
    try {
      const uploadedDocs = [];

      for (const item of uploadItems) {
        const { file, docType, category } = item;

        if (!file || !(file instanceof File) || file.size === 0) {
          return rejectWithValue(`Invalid file for ${docType || 'document'}`);
        }

        const formData = new FormData();
        formData.append('file', file);

        const response = await uploadPropertyDocumentApi(formData);
        const url = extractUploadUrl(response);

        if (!url) {
          return rejectWithValue(
            `Upload succeeded but no file URL was returned for "${file.name}". Please try again.`
          );
        }

        uploadedDocs.push({
          docType,
          category,
          fileName: file.name,
          url,
        });
      }

      return uploadedDocs;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        error.response?.data?.detail ||
        error.message ||
        'Failed to upload documents'
      );
    }
  }
);

// Async thunk for property provisioning
export const provisionProperty = createAsyncThunk(
  'properties/provisionProperty',
  async (propertyData, { rejectWithValue }) => {
    try {
      const response = await provisionPropertyApi(propertyData);
      return response.data;
    } catch (error) {

      return rejectWithValue(
        error.response?.data?.detail || 'Failed to provision property'
      );
    }
  }
);

const initialState = {
  loading: false,
  uploading: false,
  error: null,
  uploadError: null,
  success: false,
  propertyData: null,
  uploadedDocuments: [],
};

const propertiesSlice = createSlice({
  name: 'properties',
  initialState,
  reducers: {
    clearPropertyState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.propertyData = null;
      state.uploading = false;
      state.uploadError = null;
      state.uploadedDocuments = [];
    },
    clearError: (state) => {
      state.error = null;
      state.uploadError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Upload documents cases
      .addCase(uploadDocuments.pending, (state) => {
        state.uploading = true;
        state.uploadError = null;
      })
      .addCase(uploadDocuments.fulfilled, (state, action) => {
        state.uploading = false;
        state.uploadedDocuments = action.payload;
        state.uploadError = null;
      })
      .addCase(uploadDocuments.rejected, (state, action) => {
        state.uploading = false;
        state.uploadError = action.payload;
      })
      // Provision property cases
      .addCase(provisionProperty.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(provisionProperty.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.propertyData = action.payload;
        state.error = null;
      })
      .addCase(provisionProperty.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      });
  },
});

export const { clearPropertyState, clearError } = propertiesSlice.actions;
export default propertiesSlice.reducer;
